import { useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FotosTrabajo } from "@/components/FotosTrabajo";
import { numTrabajo, fmtFecha } from "@/pages/TrabajosActivos";
import { textoAgendaNextep, AGENDA_NEXTEP_COLOR_CLASS } from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";

const fmtMiles = (n) => (n === null || n === undefined ? "" : Number(n).toLocaleString("es-CR"));

const ACCION_LABEL = {
  revisar: "Revisar", limpiar: "Limpiar", cambiar: "Cambiar",
  ajustar: "Ajustar", lubricar: "Lubricar", hecho: "Hecho", na: "N/A",
};

// Resumen rápido, de solo lectura, de un trabajo (tareas, cronograma, checklist). Se usa
// desde "Trabajos activos" (mostrarRealizados=false) y "Trabajos realizados" (true).
export function ResumenTrabajoDialog({ trabajoId, mostrarRealizados, onClose }) {
  const { isAdmin } = useAuth();
  const [trabajo, setTrabajo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!trabajoId) { setTrabajo(null); setError(""); return; }
    setTrabajo(null);
    setCargando(true);
    setError("");
    api.get(`/trabajos/${trabajoId}`)
      .then(({ data }) => setTrabajo(data))
      .catch((e) => setError(errorMsg(e)))
      .finally(() => setCargando(false));
  }, [trabajoId]);

  return (
    <Dialog open={!!trabajoId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="resumen-trabajo-dialog" className="max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle>Orden #{trabajo ? numTrabajo(trabajo.numero) : "—"}</DialogTitle>
        </DialogHeader>
        {cargando ? (
          <p data-testid="resumen-trabajo-cargando" className="text-sm text-slate-500">Cargando…</p>
        ) : error ? (
          <p data-testid="resumen-trabajo-error" className="text-sm text-red-600">{error}</p>
        ) : trabajo ? (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="text-sm text-slate-600 space-y-0.5">
              <p>
                <span className="font-medium text-slate-800">{trabajo.vehiculo?.placa || "—"}</span>
                {" · "}{trabajo.vehiculo?.modelo || trabajo.modelo || "—"}
                {" · "}{trabajo.cliente?.nombre || "—"}
              </p>
              <p className="text-xs text-slate-500">Ingreso: {fmtFecha(trabajo.fecha_ingreso)}</p>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Kilometraje registrado</p>
              <p data-testid="resumen-trabajo-kilometraje" className="text-sm text-slate-800">
                {trabajo.medir_kilometraje === false ? "No se midió" : `${fmtMiles(trabajo.kilometraje)} km`}
              </p>
            </div>

            {textoAgendaNextep(trabajo) && (() => {
              const agendaInfo = textoAgendaNextep(trabajo);
              return (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Cita previa</p>
                  <p data-testid="resumen-trabajo-agenda-nextep"
                    className={`text-sm ${AGENDA_NEXTEP_COLOR_CLASS[agendaInfo.color] || "text-slate-800"}`}>
                    {agendaInfo.texto}
                  </p>
                  {agendaInfo.detalle && (
                    <p className="text-xs text-slate-500 mt-0.5">{agendaInfo.detalle}</p>
                  )}
                </div>
              );
            })()}

            {trabajo.es_cupon && (
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Orden Honda</p>
                {trabajo.orden_honda ? (
                  <p data-testid="resumen-trabajo-orden-honda" className="text-sm text-slate-800">
                    {trabajo.orden_honda}
                  </p>
                ) : trabajo.orden_honda_pendiente ? (
                  <div data-testid="resumen-trabajo-orden-honda" className="text-sm text-amber-700">
                    <p>
                      Pendiente
                      {trabajo.orden_honda_pendiente.tipo === "otro"
                        ? ` — ${trabajo.orden_honda_pendiente.texto}`
                        : trabajo.orden_honda_pendiente.orden_bloqueante
                          ? ` — orden bloqueante #${trabajo.orden_honda_pendiente.orden_bloqueante}`
                          : " — otra orden abierta en Honda"}
                    </p>
                    <p className="text-xs text-amber-600/80">Desde {fmtFecha(trabajo.orden_honda_pendiente.desde)}</p>
                  </div>
                ) : (
                  <p data-testid="resumen-trabajo-orden-honda" className="text-sm text-amber-600">Sin registrar</p>
                )}
              </div>
            )}

            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 mb-1.5">Trabajos a realizar</p>
              <div className="space-y-2.5">
                {(trabajo.tareas || []).map((t) => {
                  const lineasCrono = (t.hijos || []).filter((h) => h.se_realiza);
                  return (
                    <div key={t.id} data-testid={`resumen-tarea-${t.id}`} className="border border-slate-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800 text-sm">{t.servicio?.nombre || "—"}</span>
                        {t.aprobacion === "no_aprobado" && (
                          <span data-testid={`resumen-tarea-no-aprobado-${t.id}`}
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold uppercase">
                            No aprobado
                          </span>
                        )}
                        {t.nueva && (
                          <span data-testid={`resumen-tarea-nueva-${t.id}`}
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold uppercase">
                            Agregada después
                          </span>
                        )}
                      </div>
                      {t.detalle && <p className="text-xs text-slate-500 mt-0.5">{t.detalle}</p>}
                      {lineasCrono.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5 pl-3 list-disc text-xs text-slate-600">
                          {lineasCrono.map((h) => (
                            <li key={h.hijo_id}>{h.nombre} — {ACCION_LABEL[h.accion] || h.accion}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                {(trabajo.tareas || []).length === 0 && (
                  <p data-testid="resumen-tareas-vacio" className="text-sm text-slate-400">Sin tareas registradas</p>
                )}
              </div>
            </div>

            {mostrarRealizados && (
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500 mb-1.5">Trabajos realizados</p>
                <div className="space-y-2.5">
                  {(trabajo.tareas || []).map((t) => {
                    const checklist = t.servicio?.hijos || [];
                    const realizados = new Set(t.hijos_realizados || []);
                    return (
                      <div key={t.id} data-testid={`resumen-tarea-realizado-${t.id}`} className="border border-slate-200 rounded-lg p-2.5">
                        <div className="flex items-center gap-1.5 text-sm">
                          {t.realizado ? (
                            <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                          ) : (
                            <XCircle size={14} className="text-slate-400 shrink-0" />
                          )}
                          <span className={t.realizado ? "font-medium text-slate-800" : "text-slate-500"}>
                            {t.servicio?.nombre || "—"}
                          </span>
                          {!t.realizado && <span className="text-xs text-slate-400">(No realizado)</span>}
                        </div>
                        {checklist.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5 pl-5 text-xs text-slate-600">
                            {checklist.map((h) => (
                              <li key={h.id} className="flex items-center gap-1.5">
                                {realizados.has(h.id) ? (
                                  <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />
                                ) : (
                                  <XCircle size={12} className="text-slate-300 shrink-0" />
                                )}
                                {h.nombre}
                              </li>
                            ))}
                          </ul>
                        )}
                        {t.nota && (
                          <p data-testid={`resumen-tarea-nota-${t.id}`} className="text-xs text-slate-500 italic mt-1.5">
                            Nota: {t.nota}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {(trabajo.tareas || []).length === 0 && (
                    <p className="text-sm text-slate-400">Sin tareas registradas</p>
                  )}
                </div>
              </div>
            )}
            {isAdmin && (
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500 mb-1.5">Fotos</p>
                <FotosTrabajo trabajoId={trabajo.id} momento="ingreso" soloLectura />
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
